/**
 * CARTIE Lens — Cost Linter for App Code (Patent Sub-system H)
 * =============================================================
 * Paste Python or JS source → see cost antipatterns + $-impact + suggested fix.
 * SonarQube lints for code quality. Snyk for security. We lint for COST.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ScanLine, FileCode2, AlertTriangle, TrendingDown, Sparkles, Loader2,
  CheckCircle2, ExternalLink, Code2, FlaskConical,
} from 'lucide-react';
import { toast } from 'sonner';
import DemoDataBanner from '../components/DemoDataBanner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUsd = (n) => {
  if (n == null) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
};

const SEVERITY_STYLE = {
  high: { color: 'text-red-300', bg: 'bg-red-500/10 border-red-500/30', dot: 'bg-red-400' },
  medium: { color: 'text-amber-300', bg: 'bg-amber-500/10 border-amber-500/30', dot: 'bg-amber-400' },
  low: { color: 'text-sky-300', bg: 'bg-emerald-500/10 border-sky-500/30', dot: 'bg-sky-400' },
};

const CartieLens = () => {
  const [code, setCode] = useState('');
  const [language, setLanguage] = useState('python');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [signatures, setSignatures] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/cartie-lens/signatures`)
      .then((r) => setSignatures(r.data.signatures || []))
      .catch(() => { /* non-critical */ });
  }, []);

  const loadExample = async () => {
    try {
      const r = await axios.get(`${API_URL}/cartie-lens/example`);
      setCode(r.data.code);
      setLanguage(r.data.language);
      toast.success('Example loaded — click Scan to see findings');
    } catch (e) {
      toast.error('Could not load example');
    }
  };

  const scan = async () => {
    if (!code.trim()) {
      toast.error('Paste some code first');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.post(`${API_URL}/cartie-lens/scan`, { code, language });
      setResult(r.data);
      if (r.data.findings.length === 0) {
        toast.success('No cost antipatterns detected — clean!');
      } else {
        toast.warning(`${r.data.findings.length} cost issue${r.data.findings.length > 1 ? 's' : ''} found · ${fmtUsd(r.data.total_projected_monthly_usd)}/mo`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100 relative overflow-hidden" data-testid="cartie-lens-page">
      <Helmet>
        <title>CARTIE Lens — Cost-as-a-Lint-Rule for App Code | CARTIEAI</title>
        <meta name="description" content="The first cost linter for application code. Paste Python or JavaScript, see cost antipatterns + projected $-impact. SonarQube lints quality. Snyk lints security. CARTIE Lens lints cost." />
      </Helmet>

      {/* Atmospheric orbs — match the rest of the marketing free-tools tier */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-10 left-1/4 w-[420px] h-[420px] rounded-full bg-emerald-500/10 blur-[120px]" />
        <div className="absolute top-40 right-1/4 w-[380px] h-[380px] rounded-full bg-teal-500/10 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 relative">
        <DemoDataBanner />
        {/* HERO */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-500/30 rounded-md px-2 py-1 mb-5" data-testid="lens-patent-badge">
            <FlaskConical className="w-3 h-3" />
            Proprietary engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]"><BrandTitle>CARTIE Lens</BrandTitle></h1>
          <p className="mt-4 text-base sm:text-lg text-slate-400 leading-relaxed">
            The first <span className="text-white font-semibold">cost linter for application code</span>. SonarQube lints code quality. Snyk lints security. <span className="text-emerald-300">We lint cost.</span>
          </p>
          <p className="mt-3 text-sm text-slate-500">
            Paste Python or JavaScript → see every cost-expensive pattern (LLM-in-loop, missing pagination, abandoned streams, retry storms) with the projected dollar-impact and a one-line fix.
          </p>
        </div>

        {/* MAIN UI */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* PASTE PANEL */}
          <Card className="lg:col-span-3 bg-slate-900/60 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <FileCode2 className="w-5 h-5 text-emerald-300" /> Paste your code
              </CardTitle>
              <CardDescription>Up to 50,000 characters. Nothing is stored unless you sign in and click "Scan & Save".</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className="w-40 bg-slate-950 border-white/10 text-white" data-testid="lens-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10 text-white">
                    <SelectItem value="python">Python</SelectItem>
                    <SelectItem value="javascript">JavaScript</SelectItem>
                    <SelectItem value="typescript">TypeScript</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" onClick={loadExample} className="text-emerald-300 hover:text-emerald-200" data-testid="lens-load-example-btn">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Load example
                </Button>
              </div>
              <Textarea
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="# Paste your Python or JS code here&#10;# Example: an AI function that calls OpenAI/Anthropic in a loop&#10;for ticket in tickets:&#10;    response = openai.chat.completions.create(...)"
                className="font-mono text-xs bg-slate-950 border-white/10 text-white min-h-[360px]"
                data-testid="lens-code-input"
              />
              <Button
                onClick={scan}
                disabled={loading || !code.trim()}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
                data-testid="lens-scan-btn"
              >
                {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning…</>) : (<><ScanLine className="w-4 h-4 mr-2" /> Scan for cost antipatterns</>)}
              </Button>
            </CardContent>
          </Card>

          {/* RESULTS PANEL */}
          <Card className="lg:col-span-2 bg-slate-900/60 border-white/10" data-testid="lens-results-panel">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-emerald-300" /> Findings
              </CardTitle>
              <CardDescription>Each finding has a fix + projected $-impact at observed traffic.</CardDescription>
            </CardHeader>
            <CardContent>
              {!result && (
                <div className="text-center py-14 text-slate-500 text-sm">
                  <Code2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  Paste code on the left, hit Scan.
                </div>
              )}
              {result && result.findings.length === 0 && (
                <div className="text-center py-12 text-emerald-400" data-testid="lens-result-clean">
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-3" />
                  <div className="font-semibold">Clean — no cost antipatterns found.</div>
                  <div className="text-xs text-slate-500 mt-1">Scanned {result.lines_scanned} lines.</div>
                </div>
              )}
              {result && result.findings.length > 0 && (
                <div className="space-y-3" data-testid="lens-result-findings">
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <div className="bg-slate-950 border border-white/5 rounded-md p-2 text-center">
                      <div className="text-[10px] uppercase text-slate-500">Monthly</div>
                      <div className="text-emerald-300 font-mono font-semibold">{fmtUsd(result.total_projected_monthly_usd)}</div>
                    </div>
                    <div className="bg-slate-950 border border-white/5 rounded-md p-2 text-center">
                      <div className="text-[10px] uppercase text-slate-500">Annual</div>
                      <div className="text-emerald-300 font-mono font-semibold">{fmtUsd(result.total_projected_annual_usd)}</div>
                    </div>
                    <div className="bg-slate-950 border border-white/5 rounded-md p-2 text-center">
                      <div className="text-[10px] uppercase text-slate-500">Issues</div>
                      <div className="text-white font-mono font-semibold">{result.findings.length}</div>
                    </div>
                  </div>
                  {result.findings.map((f, idx) => {
                    const sev = SEVERITY_STYLE[f.severity] || SEVERITY_STYLE.low;
                    return (
                      <div key={idx} className={`rounded-md border ${sev.bg} p-3`} data-testid={`lens-finding-${f.signature_id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className={`w-1.5 h-1.5 rounded-full ${sev.dot}`} />
                            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
                              {f.signature_id} · line {f.line}
                            </span>
                          </div>
                          <span className={`text-[10px] font-semibold uppercase ${sev.color}`}>{f.severity}</span>
                        </div>
                        <div className="text-white font-semibold text-sm mt-1.5">{f.title}</div>
                        <code className="block text-[11px] text-slate-400 bg-slate-950 rounded mt-2 px-2 py-1 overflow-x-auto font-mono">{f.snippet || '(snippet unavailable)'}</code>
                        <div className="text-xs text-slate-600 mt-2 leading-relaxed">{f.description}</div>
                        <div className="text-xs text-emerald-300 mt-2 flex items-start gap-1.5">
                          <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span><span className="font-semibold">Fix:</span> {f.fix}</span>
                        </div>
                        <div className="text-xs text-amber-300 mt-1.5 flex items-center gap-1.5">
                          <AlertTriangle className="w-3 h-3" />
                          Projected impact: <span className="font-mono font-semibold">{fmtUsd(f.projected_monthly_usd)}/mo</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* SIGNATURE CATALOG */}
        <div className="mt-16">
          <h2 className="text-base font-semibold tracking-widest text-emerald-300 uppercase mb-2">The catalog</h2>
          <p className="text-sm text-slate-400 mb-6 max-w-2xl">10 cost-pattern signatures live today. Each one is a real anti-pattern we&apos;ve seen in production codebases — and a documented $-impact at observed traffic.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="lens-catalog">
            {signatures.map((s) => {
              const sev = SEVERITY_STYLE[s.severity] || SEVERITY_STYLE.low;
              return (
                <div key={s.id} className="bg-slate-900/60 border border-white/10 rounded-md p-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono uppercase text-slate-500">{s.id}</span>
                      <Badge variant="outline" className="text-[10px] border-white/10 text-slate-400">{s.language}</Badge>
                      <span className={`w-1.5 h-1.5 rounded-full ${sev.dot}`} />
                      <span className={`text-[10px] font-semibold uppercase ${sev.color}`}>{s.severity}</span>
                    </div>
                  </div>
                  <div className="text-white font-semibold text-sm">{s.title}</div>
                  <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">{s.description}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CI INTEGRATION TEASER */}
        <Card className="mt-16 bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border-emerald-500/30">
          <CardContent className="p-6 sm:p-8">
            <div className="text-xs uppercase tracking-widest text-emerald-300 font-semibold mb-2">Coming next</div>
            <h3 className="text-2xl font-bold text-white">PR comments + VSCode squiggles + standalone CLI.</h3>
            <p className="text-slate-400 mt-2 max-w-2xl">Cost-Lens will run as a GitHub Action (15 lines of YAML) and comment on every PR. A VSCode extension surfaces findings as inline squiggles while you type. Standalone CLI binary for local pre-commit.</p>
            <div className="mt-4 text-xs text-slate-500 flex items-center gap-1.5">
              <ExternalLink className="w-3 h-3" />
              Want early access? Join the waitlist at <a href="/vscode-extension" className="text-emerald-300 hover:text-emerald-200 underline">/vscode-extension</a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CartieLens;
